import { useCallback, useState } from "react";
import useAdminLoad from "../../../hooks/useAdminLoad";
import * as crmApi from "../api/crmApi";

/**
 * The Email Automation grid (`GET /api/automation/sequences`): the five system sequences
 * plus any custom ones, each with its own activate/draft toggle and Duplicate.
 *
 * `toggle` is OPTIMISTIC (the switch flips at once) and rolls back on refusal — a system
 * sequence a helper cannot reach never renders the switch at all (RoleGate on the whole
 * screen), so the only refusal in practice is `SEQUENCE_EMPTY` on Activate.
 */
export default function useSequences() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const page = await crmApi.listSequences();
    setRows(Array.isArray(page?.rows) ? page.rows : []);
  }, []);
  const { loading, loadError, reload } = useAdminLoad(load);

  const toggle = useCallback(async (id, nextActive) => {
    const before = rows;
    setRows((r) => r.map((s) => (s.id === id ? { ...s, status: nextActive ? "active" : "draft" } : s)));
    try {
      const saved = nextActive ? await crmApi.activateSequence(id) : await crmApi.draftSequence(id);
      if (saved && typeof saved === "object") setRows((r) => r.map((s) => (s.id === id ? { ...s, ...saved } : s)));
    } catch (e) {
      setRows(before);
      throw e;
    }
  }, [rows]);

  const duplicate = useCallback(async (id) => {
    const copy = await crmApi.duplicateSequence(id);
    setRows((r) => [...r, copy]);
    return copy;
  }, []);

  /** "+ New sequence": a blank draft, created at once so the editor always opens on a real id. */
  const create = useCallback(async () => {
    const seq = await crmApi.createSequence({ name: "" });
    setRows((r) => [...r, seq]);
    return seq;
  }, []);

  return { rows, loading, loadError, reload, toggle, duplicate, create };
}
